import { useCallback, useEffect, useRef, useState, type MouseEvent } from 'react'
import { useIntl } from 'react-intl'
import styled, { keyframes } from 'styled-components'
import { rem } from 'polished'

import Caption from 'LuxKit/Typography/Caption'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import MessagingLabel from 'LuxKit/Label/MessagingLabel'
import NeutralLabel from 'LuxKit/Label/NeutralLabel'
import LineAngleDownIcon from 'LuxKit/Icons/line/LineAngleDownIcon'
import LineSpinnerIcon from 'LuxKit/Icons/line/LineSpinnerIcon'
import focusStyle from 'styles/utils/focusStyle'
import { getTripListStatusLabel, getTripListStatusTone } from './tripListStatus'
import type { TripListStatus, TripListStatusValue } from './tripListTypes'

const StatusButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  gap: ${rem(8)};
  max-width: 100%;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;

  &:hover .status-chevron,
  &:focus-visible .status-chevron,
  &.is-open .status-chevron {
    opacity: 1;
  }

  &:disabled {
    cursor: default;
  }
`

const StatusChevronIcon = styled(LineAngleDownIcon)`
  flex-shrink: 0;
  opacity: 0;
  transition: opacity 120ms ease;
`

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`

const SpinnerIcon = styled(LineSpinnerIcon)`
  animation: ${spin} 800ms linear infinite;
`

const StatusMenuContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  min-width: ${rem(160)};
`

const StatusMenuHeading = styled(Caption)`
  padding: 0 ${rem(16)} ${rem(8)};
`

const StatusOptionButton = styled.button`
  display: inline-flex;
  align-items: center;
  width: 100%;
  padding: ${rem(8)} ${rem(16)};
  border: 0;
  background: transparent;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    background: ${props => props.theme.palette.neutral.surface.lightBackground};
  }

  &:focus-visible {
    ${focusStyle}
  }

  &:disabled {
    cursor: default;
  }
`

interface Props {
  status: TripListStatus
  title: string
  disabled: boolean
  saving: boolean
  onChange: (status: TripListStatusValue) => void
}

interface StatusLabelProps {
  status: TripListStatus
  saving: boolean
}

interface StatusOptionProps {
  option: TripListStatusValue
  title: string
  onSelect: (status: TripListStatusValue) => void
}

function StatusLabel({ status, saving }: StatusLabelProps) {
  return <StatusValueLabel value={status.value} label={status.label} saving={saving} />
}

function StatusValueLabel({ value, label, saving = false }: { value: TripListStatusValue, label: string, saving?: boolean }) {
  const endIcon = saving ? <SpinnerIcon size="XS" /> : undefined
  const tone = getTripListStatusTone(value)

  if (tone === 'positive') {
    return <MessagingLabel variant="success" endIcon={endIcon}>{label}</MessagingLabel>
  }

  if (tone === 'idea') {
    return <MessagingLabel variant="warning" endIcon={endIcon}>{label}</MessagingLabel>
  }

  return <NeutralLabel endIcon={endIcon}>{label}</NeutralLabel>
}

function StatusOption({ option, title, onSelect }: StatusOptionProps) {
  const handleClick = useCallback((event: MouseEvent) => {
    event.stopPropagation()
    onSelect(option)
  }, [onSelect, option])

  const stopControlEvent = useCallback((event: MouseEvent) => {
    event.stopPropagation()
  }, [])

  return (
    <StatusOptionButton
      type="button"
      onClick={handleClick}
      onMouseDown={stopControlEvent}
    >
      <StatusValueLabel value={option} label={title} />
    </StatusOptionButton>
  )
}

function TripListRowStatus({ status, title, disabled, saving, onChange }: Props) {
  const intl = useIntl()
  const [menuOpen, setMenuOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const isInteractive = status.isEditable && !disabled && !saving

  useEffect(() => {
    if (!isInteractive) setMenuOpen(false)
  }, [isInteractive])

  const closeMenu = useCallback(() => {
    setMenuOpen(false)
  }, [])

  const handleTriggerClick = useCallback((event: MouseEvent) => {
    event.stopPropagation()
    if (!isInteractive) return
    setMenuOpen(prev => !prev)
  }, [isInteractive])

  const stopControlEvent = useCallback((event: MouseEvent) => {
    event.stopPropagation()
  }, [])

  const handleStatusSelect = useCallback((nextStatus: TripListStatusValue) => {
    closeMenu()
    if (nextStatus === status.value) return
    onChange(nextStatus)
  }, [closeMenu, onChange, status.value])

  const statusLabel = intl.formatMessage(
    {
      id: 'rR9r7hDPHcrU4UNxn_ls',
      defaultMessage: 'Status for {title}',
      description: 'Accessible label for the editable status field in the TripChat itinerary list view',
    },
    { title },
  )
  const changeStatusLabel = intl.formatMessage({
    id: '8RPe9eVikylFXDYrxDNN',
    defaultMessage: 'Change status',
    description: 'Heading shown above status options in the TripChat itinerary list status dropdown',
  })
  const statusOptions = status.options.filter(option => option !== status.value)

  return (
    <>
      <StatusButton
        ref={triggerRef}
        type="button"
        disabled={!isInteractive}
        aria-label={statusLabel}
        aria-expanded={menuOpen}
        className={menuOpen ? 'is-open' : undefined}
        onClick={handleTriggerClick}
        onMouseDown={stopControlEvent}
      >
        <StatusLabel status={status} saving={saving} />
        {isInteractive && <StatusChevronIcon className="status-chevron" size="XS" aria-hidden />}
      </StatusButton>
      <DropdownList
        size="S"
        open={menuOpen}
        onClose={closeMenu}
        triggerRef={triggerRef}
        anchorRef={triggerRef}
        placement="bottom-start"
        enableFlip
        portaled
      >
        <StatusMenuContent>
          <StatusMenuHeading variant="medium" weight="bold">{changeStatusLabel}</StatusMenuHeading>
          {statusOptions.map(option => (
            <StatusOption
              key={option}
              option={option}
              title={getTripListStatusLabel(intl, option)}
              onSelect={handleStatusSelect}
            />
          ))}
        </StatusMenuContent>
      </DropdownList>
    </>
  )
}

export default TripListRowStatus
